import { Controller } from "@hotwired/stimulus"

// Powers shared/_file_drop_slot.html.haml — the non-image sibling of the
// image-preview upload slot (audit F59 tier 2). The real file input is
// stretched invisibly across the drop zone, so click-to-browse is native;
// this controller adds drag-and-drop, a highlight state while dragging, and
// selected-filename chips with per-file remove buttons.
//
// Kept separate from image_preview_controller on purpose: that controller's
// whole job is swapping an <img> preview in place, while this one manages a
// FileList (drag state, chip rendering, per-file removal) — merging them
// would conflate two unrelated concerns behind one identifier.
export default class extends Controller {
  static targets = ["zone", "input", "chips"]

  connect() {
    this.dragDepth = 0
  }

  // ── Drag & drop ──────────────────────────────────────────────────────────

  dragEnter(event) {
    if (!this.containsFiles(event)) return
    event.preventDefault()
    this.dragDepth++
    this.zoneTarget.classList.add("gl-file-dropzone--dragover")
  }

  dragOver(event) {
    if (!this.containsFiles(event)) return
    event.preventDefault()
  }

  dragLeave(event) {
    if (!this.containsFiles(event)) return
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.zoneTarget.classList.remove("gl-file-dropzone--dragover")
  }

  drop(event) {
    if (!this.containsFiles(event)) return
    event.preventDefault()
    this.dragDepth = 0
    this.zoneTarget.classList.remove("gl-file-dropzone--dragover")

    const dropped = Array.from(event.dataTransfer.files || [])
    if (dropped.length === 0) return
    // Mirror the native picker: a new selection REPLACES the old one, and a
    // single-file input takes only the first dropped file.
    this.assignFiles(this.inputTarget.multiple ? dropped : [dropped[0]])
  }

  // ── Selection & chips ────────────────────────────────────────────────────

  filesChanged() {
    this.renderChips()
  }

  removeFile(event) {
    const index = Number(event.currentTarget.dataset.index)
    this.assignFiles(this.currentFiles().filter((_, i) => i !== index))
  }

  assignFiles(files) {
    try {
      const transfer = new DataTransfer()
      files.forEach((file) => transfer.items.add(file))
      this.inputTarget.files = transfer.files
    } catch {
      // DataTransfer construction unsupported — clear rather than desync the
      // chips from what the form would actually submit.
      this.inputTarget.value = ""
    }
    // Bubbling change keeps chips AND any extra change listeners (dirty
    // tracking etc.) in sync with programmatic file-list edits.
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  renderChips() {
    this.chipsTarget.replaceChildren(
      ...this.currentFiles().map((file, index) => this.buildChip(file, index))
    )
  }

  buildChip(file, index) {
    const chip = document.createElement("div")
    chip.className = "gl-file-row gl-file-row--compact"

    const icon = document.createElement("i")
    icon.className = `ph ${this.iconFor(file)} gl-file-row-icon`

    const body = document.createElement("div")
    body.className = "gl-file-row-body"
    const name = document.createElement("div")
    name.className = "gl-file-row-name"
    name.textContent = file.name
    const meta = document.createElement("div")
    meta.className = "gl-file-row-meta"
    meta.textContent = this.humanFileSize(file.size)
    body.append(name, meta)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "gl-file-row-remove"
    remove.setAttribute("aria-label", `Remove ${file.name}`)
    remove.dataset.index = String(index)
    remove.dataset.action = "file-drop#removeFile"
    const removeIcon = document.createElement("i")
    removeIcon.className = "ph ph-x"
    remove.append(removeIcon)

    chip.append(icon, body, remove)
    return chip
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  containsFiles(event) {
    return Boolean(event.dataTransfer) && Array.from(event.dataTransfer.types || []).includes("Files")
  }

  currentFiles() {
    return Array.from(this.inputTarget.files || [])
  }

  iconFor(file) {
    const name = file.name.toLowerCase()
    if ((file.type || "").startsWith("image/")) return "ph-image"
    if (name.endsWith(".csv")) return "ph-file-csv"
    if (name.endsWith(".pdf")) return "ph-file-pdf"
    if (name.endsWith(".doc") || name.endsWith(".docx")) return "ph-file-doc"
    if (name.endsWith(".xls") || name.endsWith(".xlsx")) return "ph-file-xls"
    return "ph-file"
  }

  humanFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes < 0) return ""
    if (bytes < 1024) return `${bytes} B`
    const units = ["KB", "MB", "GB"]
    let value = bytes
    let unit = "B"
    for (const next of units) {
      if (value < 1024) break
      value /= 1024
      unit = next
    }
    const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10
    return `${rounded} ${unit}`
  }
}
